import { Tag } from "@/types/v2/Tags";

/**
 * Orders a card's tag labels by the shop's shared tag order.
 *
 * The shop's tag list (`shops/{shopId}/misc/tags`) is an ordered array — its
 * index *is* the order, and reordering in Settings rewrites it. Cards store
 * their tags as plain labels in whatever order they happened to be selected,
 * so every render site pipes them through here to get the shop's order.
 *
 * Labels the shop doesn't know about (a card tag whose shop tag was deleted,
 * or one that arrived from a sync) keep their relative order and land after
 * all the known ones, so nothing silently disappears.
 */
export const sortTagsByShopOrder = (
  cardTags: string[],
  shopTags?: Tag[]
): string[] => {
  if (!shopTags?.length) return [...cardTags];

  const orderByLabel = new Map<string, number>();
  shopTags.forEach((tag, index) => {
    const key = tag.label.toLowerCase();
    if (!orderByLabel.has(key)) orderByLabel.set(key, index);
  });

  return cardTags
    .map((label, index) => ({
      label,
      index,
      order: orderByLabel.get(label.toLowerCase()) ?? Number.POSITIVE_INFINITY,
    }))
    .sort((a, b) => a.order - b.order || a.index - b.index)
    .map(({ label }) => label);
};
